// src/components/resources/tools/generators/AiChat/ClaudeThread.tsx

import type { JSX } from 'react'
import { cn } from '~/lib/utils'
import type { AiChatMessage, AiChatSettings } from './AiChat.types'
import { renderMessageContent, type MessageContentStyles } from './renderMessageContent'
import { ClaudeLogo } from './BrandLogos'
import { ChevronDownIcon, CopyIcon, MoreIcon, PlusIcon, RefreshIcon, SendArrowIcon, ThumbDownIcon, ThumbUpIcon } from './Icons'

/**
 * Pixel-faithful recreation of the Claude conversation surface (2026
 * claude.ai web UI): the warm ivory canvas, a model-selector header, a centred
 * transcript with right-aligned cream user bubbles and full-width assistant
 * turns led by the clay starburst mark, the response action row, and the
 * rounded "Reply to Claude…" composer with its clay send button.
 */

interface ThreadProps {
  settings: AiChatSettings
  messages: AiChatMessage[]
}

/** Anthropic clay accents — logo tone vs the slightly deeper button tone. */
const CLAY = '#C96442'

function contentStyles(dark: boolean): MessageContentStyles {
  return {
    paragraph: 'my-3.5 first:mt-0 last:mb-0 leading-[1.7]',
    heading: 'my-3.5 text-[19px] font-semibold first:mt-0',
    inlineCode: cn(
      'rounded px-1.5 py-0.5 font-mono text-[13.5px]',
      dark ? 'bg-[#1f1e1d] text-[#e8c9a8]' : 'bg-[#ece9e0] text-[#9a5a3b]',
    ),
    codeBlock: cn('my-3.5 overflow-hidden rounded-xl border text-[#e6edf3]', dark ? 'border-[#3a3a37] bg-[#1f1e1d]' : 'border-[#e3e0d6] bg-[#0d0d0c]'),
    codeBlockHeader: 'border-b border-white/10 bg-white/5 px-4 py-1.5 text-[12px] font-medium text-white/55',
    bulletMarker: dark ? 'bg-[#b0ada0]' : 'bg-[#8a8779]',
    orderedMarker: 'font-medium',
    strong: 'font-semibold',
    link: 'underline decoration-1 underline-offset-2',
  }
}

// Precompute both themes once at module load — see ChatGptThread for rationale.
const CONTENT_STYLES = { light: contentStyles(false), dark: contentStyles(true) } as const

export function ClaudeThread({ settings, messages }: ThreadProps): JSX.Element {
  const dark = settings.theme === 'dark'
  const styles = CONTENT_STYLES[settings.theme]

  const canvas = dark ? 'bg-[#262624] text-[#f5f4ee]' : 'bg-[#faf9f5] text-[#3d3a32]'
  const userBubble = dark ? 'bg-[#1f1e1d] text-[#f5f4ee]' : 'bg-[#f0eee6] text-[#3d3a32]'
  const muted = dark ? 'text-[#a8a59b]' : 'text-[#8a8779]'
  const composerSurface = dark ? 'bg-[#30302e] border-[#46453f]' : 'bg-white border-[#e6e3d8]'

  return (
    <div className={cn('flex h-full flex-col font-sans antialiased', canvas)} style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif' }}>
      {settings.showHeader ? (
        <header className="flex items-center justify-between px-4 py-3">
          {/* Decorative mock chrome — a <div>, not a <button> (no tab stop). */}
          <div className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[15px] font-medium">
            <ClaudeLogo className="h-[18px] w-[18px]" />
            <span>{settings.modelLabel}</span>
            <ChevronDownIcon className={cn('h-4 w-4', muted)} />
          </div>
          {settings.showAvatars ? (
            <span className={cn('flex h-7 w-7 items-center justify-center rounded-md text-[12px] font-semibold text-white', settings.userAvatarColor)}>
              {settings.userInitials}
            </span>
          ) : null}
        </header>
      ) : null}

      <div className="flex-1 overflow-y-auto px-4 pb-2 pt-2">
        <div className="mx-auto flex w-full max-w-[720px] flex-col gap-1">
          {messages.map(message =>
            message.role === 'user' ? (
              <div key={message.id} className="flex justify-end py-2.5">
                <div className={cn('max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-[16px] leading-[1.6]', userBubble)}>
                  {message.content}
                </div>
              </div>
            ) : (
              <div key={message.id} className="flex gap-3.5 py-4">
                {settings.showAvatars ? (
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center">
                    <ClaudeLogo className="h-[22px] w-[22px]" />
                  </div>
                ) : null}
                <div className="min-w-0 flex-1">
                  <div className="text-[16px]">{renderMessageContent(message.content, styles)}</div>
                  {settings.showActions ? (
                    <div className={cn('mt-2 flex items-center gap-1', muted)}>
                      {[CopyIcon, ThumbUpIcon, ThumbDownIcon, RefreshIcon].map((Icon, index) => (
                        <span key={index} className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/5">
                          <Icon className="h-4 w-4" />
                        </span>
                      ))}
                      <span className="ml-1 flex items-center gap-1 rounded-md px-2 py-1 text-[12.5px] hover:bg-black/5">
                        Retry
                        <ChevronDownIcon className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            ),
          )}
        </div>
      </div>

      <div className="px-4 pb-4 pt-1">
        <div className="mx-auto w-full max-w-[720px]">
          <div className={cn('rounded-2xl border px-3 pb-2.5 pt-3 shadow-sm', composerSurface)}>
            <p className={cn('px-1 pb-6 text-[16px]', muted)}>Reply to Claude…</p>
            <div className="flex items-center justify-between">
              <span className={cn('flex h-8 w-8 items-center justify-center rounded-lg border', dark ? 'border-[#46453f] text-[#a8a59b]' : 'border-[#e6e3d8] text-[#8a8779]')}>
                <PlusIcon className="h-[18px] w-[18px]" />
              </span>
              <div className="flex items-center gap-2">
                <span className={cn('flex h-8 w-8 items-center justify-center rounded-lg', muted)}>
                  <MoreIcon className="h-[18px] w-[18px]" />
                </span>
                <span
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-white"
                  style={{ backgroundColor: CLAY }}
                >
                  <SendArrowIcon className="h-[18px] w-[18px]" strokeWidth={2} />
                </span>
              </div>
            </div>
          </div>
          <p className={cn('mt-2 text-center text-[12px]', muted)}>Claude can make mistakes. Please double-check responses.</p>
        </div>
      </div>
    </div>
  )
}
